import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from 'react-query';
import quizService from '../../services/api/quiz';
import { 
  Timer, 
  ChevronLeft, 
  ChevronRight, 
  CheckCircle2, 
  XCircle, 
  Trophy, 
  AlertCircle, 
  RefreshCcw, 
  ShieldCheck, 
  HelpCircle,
  Zap,
  ArrowRight,
  Save,
  BookOpen,
  Layers
} from 'lucide-react';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useToast } from '../../components/common/Toast';
import { useAuth } from '../../contexts/AuthContext';

const QuizPage = () => {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();

  // States
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);
  const [quizScore, setQuizScore] = useState(null);
  const [showReview, setShowReview] = useState(false);
  const [reviewData, setReviewData] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [canRetake, setCanRetake] = useState(false);
  const [retakeInfo, setRetakeInfo] = useState(null);
  const [nextLessonId, setNextLessonId] = useState(null);
  const [courseId, setCourseId] = useState(null);

  // جلب بيانات الكويز
  const { data: quizData, isLoading, error } = useQuery(
    ['quiz', quizId],
    () => quizService.getQuiz(quizId),
    {
      onSuccess: (data) => {
        // ضبط الوقت التنازلي إذا كان موجوداً
        if (data.quiz?.duration_minutes) {
          setTimeLeft(data.quiz.duration_minutes * 60);
        }
        // حفظ course_id من بيانات الكويز
        if (data.quiz?.course_id) {
          setCourseId(data.quiz.course_id);
        }
      }
    }
  );

  // تعريف quiz هنا قبل استخدامه في useQuery (memoized to keep stable identity)
  const quiz = useMemo(() => quizData?.quiz || {}, [quizData]);
  const questions = quiz.questions || [];

  // جلب معلومات الدرس الحالي للحصول على الدرس التالي (لا نخزن القيمة محلياً لأننا لا نستخدمها مباشرة)
  useQuery(
    ['lesson-nav', quiz.lesson_id],
    () => quiz.lesson_id ? quizService.getLessonNavigation(quiz.lesson_id) : Promise.resolve(null),
    {
      enabled: !!quiz.lesson_id && !isLoading,
      onSuccess: (data) => {
        if (data?.navigation?.next?.id) {
          setNextLessonId(data.navigation.next.id);
        }
      }
    }
  );

  // Mutation لإرسال الإجابات
  const submitQuizMutation = useMutation(
    (submissionData) => quizService.submitQuiz(quizId, submissionData),
    {
      onSuccess: (data) => {
        setQuizScore(data.result);
        setIsSubmitted(true);
        toast.success('تم تسليم الاختبار بنجاح!');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'فشل في تسليم الاختبار');
      }
    }
  );

  const handleOptionSelect = (questionId, optionId) => {
    if (isSubmitted) return;
    setSelectedAnswers(prev => ({
      ...prev,
      [questionId]: optionId
    }));
  };

  // handleSubmit wrapped in useCallback to keep stable for effects
  const handleSubmit = useCallback(() => {
    if (isSubmitted) return;

    // حساب الوقت المستغرق
    let timeSpent = 0;
    let totalSeconds = 0;
    
    // الحصول على المدة الكلية بشكل صحيح
    const duration = quiz.duration_minutes || quizData?.quiz?.duration_minutes || 0;
    
    if (duration > 0 && timeLeft !== null && timeLeft !== undefined) {
      totalSeconds = duration * 60;
      timeSpent = totalSeconds - timeLeft;
    } else if (duration > 0) {
      // إذا كانت timeLeft غير معرّفة، افترض أن المستخدم استغرق نصف الوقت
      totalSeconds = duration * 60;
      timeSpent = totalSeconds / 2;
    }
    
    // تأكد من أن timeSpent موجب
    timeSpent = Math.max(0, timeSpent);
    
    const submissionData = {
      answers: Object.entries(selectedAnswers).map(([qId, oId]) => ({
        question_id: parseInt(qId),
        option_id: oId
      })),
      timeSpentSeconds: Math.round(timeSpent)
    };


    submitQuizMutation.mutate(submissionData);
  }, [isSubmitted, quiz, quizData, timeLeft, selectedAnswers, submitQuizMutation]);

  const handleAutoSubmit = useCallback(() => {
    toast.warning('انتهى الوقت! يتم تسليم إجاباتك الآن...');
    handleSubmit();
  }, [toast, handleSubmit]);

  // Function to load quiz review
  const loadReview = useCallback(async () => {
    if (!quizScore?.attempt_id) return;
    
    setReviewLoading(true);
    try {
      const response = await quizService.loadReview(quizId, quizScore.attempt_id);
      setReviewData(response);
      setShowReview(true);
    } catch (error) {
      toast.error('فشل في تحميل مراجعة الاختبار');
      console.error('Review error:', error);
    } finally {
      setReviewLoading(false);
    }
  }, [quizId, quizScore?.attempt_id, toast]);

  // Check if student can retake quiz
  const checkCanRetake = useCallback(async () => {
    try {
      const response = await quizService.checkCanRetake(quizId);
      if (response.success) {
        setCanRetake(response.can_retake);
        setRetakeInfo({
          attempts_used: response.attempts_used,
          max_attempts: response.max_attempts,
          remaining_attempts: response.remaining_attempts
        });
      }
    } catch (error) {
      console.error('❌ Error checking retake eligibility:', error);
    }
  }, [quizId]);

  // Handle retake action
  const handleRetake = () => {
    setIsSubmitted(false);
    setSelectedAnswers({});
    setShowReview(false);
    setReviewData(null);
    setQuizScore(null);
    setCurrentQuestionIndex(0);
    // Reinitialize timer
    const initDuration = quizData?.quiz?.duration_minutes || 0;
    setTimeLeft(initDuration > 0 ? initDuration * 60 : null);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // منطق التوقيت التنازلي
  useEffect(() => {
    if (timeLeft === null || isSubmitted) return;
    if (timeLeft === 0) {
      handleAutoSubmit();
      return;
    }

    const timerId = setInterval(() => {
      setTimeLeft(prev => {
        const newTime = prev - 1;
        // optional: track time remaining silently
        if (newTime % 10 === 0 || newTime <= 10) {
          // time update tick (silent)
        }
        return newTime;
      });
    }, 1000);

    return () => clearInterval(timerId);
  }, [timeLeft, isSubmitted, handleAutoSubmit]);

  // Check if can retake after submission
  useEffect(() => {
    if (isSubmitted) {
      checkCanRetake();
      loadReview();
    }
  }, [isSubmitted, checkCanRetake, loadReview]);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
        <LoadingSpinner size="large" />
        <p className="mt-4 text-gray-500 font-bold animate-pulse uppercase tracking-widest text-xs">جاري تجهيز الاختبار...</p>
      </div>
    );
  }

  if (error || !quizData) {
    return (
      <div className="max-w-xl mx-auto py-20 px-6 text-center">
        <div className="bg-white rounded-[2.5rem] shadow-xl p-10 border border-gray-100">
          <AlertCircle className="w-16 h-16 text-rose-500 mx-auto mb-6" />
          <h2 className="text-2xl font-black text-gray-900 mb-4">خطأ في تحميل الاختبار</h2>
          <p className="text-gray-600 mb-8 leading-relaxed">نعتذر، لم نتمكن من الوصول لبيانات هذا الاختبار حالياً.</p>
          <button onClick={() => navigate(-1)} className="bg-primary-600 text-white px-10 py-3 rounded-2xl font-bold hover:bg-primary-700 transition-all flex items-center justify-center gap-2 mx-auto shadow-lg">
            <ArrowRight size={18} /> العودة للخلف
          </button>
        </div>
      </div>
    );
  }

  // في حالة ظهور النتيجة
  if (isSubmitted && quizScore) {
    return (
      <div className="max-w-3xl mx-auto py-12 px-4 animate-in zoom-in duration-500">
        <div className="bg-white rounded-[3rem] shadow-2xl border border-gray-100 overflow-hidden relative">
          <div className="h-4 bg-gradient-to-r from-emerald-500 to-primary-600"></div>
          
          <div className="p-10 lg:p-16 text-center space-y-8">
            <div className="w-24 h-24 bg-emerald-50 rounded-[2rem] flex items-center justify-center mx-auto shadow-inner">
                <Trophy size={48} className="text-emerald-600" />
            </div>
            
            <div className="space-y-2">
                <h1 className="text-4xl font-black text-gray-900">أداء رائع، يا {user?.name}!</h1>
                <p className="text-gray-500 font-medium text-lg">لقد أتممت الاختبار بنجاح، إليك ملخص درجاتك.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="p-6 bg-gray-50 rounded-3xl border border-gray-100">
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">الدرجة النهائية</p>
                    <p className="text-3xl font-black text-primary-600">{quizScore.score}%</p>
                </div>
                <div className="p-6 bg-gray-50 rounded-3xl border border-gray-100">
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">الإجابات الصحيحة</p>
                    <p className="text-3xl font-black text-emerald-600">{quizScore.correct_count}</p>
                </div>
                <div className="p-6 bg-gray-50 rounded-3xl border border-gray-100">
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">الوقت المستغرق</p>
                    <p className="text-3xl font-black text-purple-600">{quizScore.time_spent || '--:--'}</p>
                </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 pt-8">
                <button 
                  onClick={() => {
                    if (nextLessonId) {
                      navigate(`/lessons/${nextLessonId}`);
                    } else if (courseId) {
                      navigate(`/courses/${courseId}`);
                    } else {
                      navigate('/student/courses');
                    }
                  }}
                  className="flex-1 bg-primary-600 text-white py-5 rounded-2xl font-black shadow-xl shadow-primary-100 hover:bg-primary-700 transition-all flex items-center justify-center gap-2 transform active:scale-95"
                >
                  <ArrowRight size={20} /> {nextLessonId ? 'الدرس التالي' : 'عرض الكورس'}
                </button>
                <button 
                  onClick={loadReview}
                  disabled={reviewLoading}
                  className="flex-1 bg-blue-100 text-blue-700 py-5 rounded-2xl font-black hover:bg-blue-200 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {reviewLoading ? <LoadingSpinner size="small" /> : <BookOpen size={20} />}
                  {reviewLoading ? 'جاري التحميل...' : 'مراجعة الإجابات'}
                </button>
                {canRetake ? (
                  <button 
                    onClick={handleRetake}
                    className="flex-1 bg-orange-100 text-orange-700 py-5 rounded-2xl font-black hover:bg-orange-200 transition-all flex items-center justify-center gap-2"
                  >
                    <RefreshCcw size={20} /> إعادة المحاولة
                  </button>
                ) : (
                  <button 
                    disabled
                    className="flex-1 bg-gray-100 text-gray-400 py-5 rounded-2xl font-black cursor-not-allowed opacity-50"
                  >
                    <RefreshCcw size={20} /> لقد استنفذت محاولاتك
                  </button>
                )}
            </div>

            {/* Attempts Info */}
            {retakeInfo && (
              <div className={`p-4 rounded-2xl border-2 text-center space-y-2 ${
                canRetake 
                  ? 'bg-orange-50 border-orange-200' 
                  : 'bg-red-50 border-red-200'
              }`}>
                <p className={`font-black text-sm ${canRetake ? 'text-orange-700' : 'text-red-700'}`}>
                  المحاولة {retakeInfo.attempts_used} من {retakeInfo.max_attempts}
                </p>
                {canRetake && (
                  <p className="text-xs text-orange-600 font-bold">
                    لديك {retakeInfo.remaining_attempts} محاولات متبقية
                  </p>
                )}
              </div>
            )}

            {/* Review Section */}
            {showReview && reviewData && (
              <div className="mt-12 space-y-6 border-t border-gray-200 pt-12">
                <h2 className="text-2xl font-black text-gray-900 flex items-center gap-2">
                  <BookOpen className="text-primary-600" size={28} />
                  مراجعة الإجابات
                </h2>

                {reviewData.review?.map((item, idx) => (
                  <div key={idx} className={`p-6 rounded-2xl border-2 transition-all ${
                    item.is_correct 
                      ? 'border-green-200 bg-green-50' 
                      : 'border-red-200 bg-red-50'
                  }`}>
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="font-black text-gray-900 mb-2 text-right">
                          السؤال {item.order}: {item.question_text}
                        </h3>
                      </div>
                      <div className={`px-4 py-2 rounded-xl font-black text-sm flex items-center gap-2 ${
                        item.is_correct 
                          ? 'bg-green-200 text-green-700' 
                          : 'bg-red-200 text-red-700'
                      }`}>
                        {item.is_correct ? (
                          <>
                            <CheckCircle2 size={18} />
                            إجابة صحيحة
                          </>
                        ) : (
                          <>
                            <XCircle size={18} />
                            إجابة خاطئة
                          </>
                        )}
                      </div>
                    </div>

                    <div className="space-y-3">
                      {item.student_answer && (
                        <div className="bg-white/60 p-4 rounded-lg text-right">
                          <p className="text-xs font-black text-gray-600 mb-1">إجابتك:</p>
                          <p className="text-gray-800 font-bold">{item.student_answer.text}</p>
                        </div>
                      )}

                      {item.correct_answer && !item.is_correct && (
                        <div className="bg-green-100 p-4 rounded-lg text-right border border-green-300">
                          <p className="text-xs font-black text-green-700 mb-1">الإجابة الصحيحة:</p>
                          <p className="text-green-900 font-bold">{item.correct_answer.text}</p>
                        </div>
                      )}

                      {item.explanation && (
                        <div className="bg-blue-50 p-4 rounded-lg text-right border border-blue-200">
                          <p className="text-xs font-black text-blue-700 mb-1">الشرح:</p>
                          <p className="text-blue-900 text-sm leading-relaxed">{item.explanation}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];

  return (
    <div className="max-w-5xl mx-auto py-8 px-4 sm:px-6 lg:px-8 space-y-8 animate-in fade-in duration-700">
      
      {/* Header Info */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div className="space-y-1">
            <h1 className="text-3xl font-black text-gray-900 tracking-tight">{quiz.title}</h1>
            {quiz.description && (
              <p className="text-gray-600 mt-2 leading-relaxed whitespace-pre-wrap">{quiz.description}</p>
            )}
            <p className="text-gray-500 font-medium flex items-center gap-2 mt-2">
                <BookOpen size={18} className="text-primary-600" />
                الدرس: {quiz.lesson_title}
            </p>
        </div>

        {/* Dynamic Timer */}
        {timeLeft !== null && (
            <div className={`
                flex items-center gap-4 px-8 py-4 rounded-[2rem] border shadow-2xl transition-all duration-500
                ${timeLeft < 60 ? 'bg-rose-50 border-rose-100 text-rose-600 animate-pulse' : 'bg-white border-gray-100 text-gray-900'}
            `}>
                <Timer size={24} className={timeLeft < 60 ? 'animate-spin-slow' : ''} />
                <div className="text-right">
                    <p className="text-[9px] font-black uppercase tracking-widest opacity-60">الوقت المتبقي</p>
                    <p className="text-xl font-black font-mono leading-none">{formatTime(timeLeft)}</p>
                </div>
            </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        
        {/* Left Column: The Questions (8 Columns) */}
        <div className="lg:col-span-8 space-y-6">
            
            {/* Progress Bar */}
            <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100 space-y-4">
                <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-widest text-gray-400">
                    <span>السؤال رقم {currentQuestionIndex + 1}</span>
                    <span>من أصل {questions.length} أسئلة</span>
                </div>
                <div className="w-full h-2.5 bg-gray-50 rounded-full overflow-hidden border border-gray-100">
                    <div 
                        className="h-full bg-primary-600 transition-all duration-500 shadow-[0_0_10px_rgba(37,99,235,0.3)]"
                        style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
                    />
                </div>
            </div>

            {/* Question Card */}
            <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8 lg:p-12 relative overflow-hidden group">
                <div className="absolute top-0 right-0 w-2 h-full bg-primary-600 opacity-20"></div>
                
                <div className="space-y-10">
                    <h2 className="text-2xl font-black text-gray-900 leading-relaxed">
                        {currentQuestion?.text}
                    </h2>

                    <div className="grid grid-cols-1 gap-4">
                        {currentQuestion?.options?.map((option, idx) => (
                            <button
                                key={option.id}
                                onClick={() => handleOptionSelect(currentQuestion.id, option.id)}
                                className={`
                                    group relative flex items-center gap-6 p-6 rounded-[1.8rem] border-2 transition-all duration-300 text-right
                                    ${selectedAnswers[currentQuestion.id] === option.id 
                                        ? 'border-primary-600 bg-primary-50/50 shadow-lg' 
                                        : 'border-gray-50 bg-gray-50/50 hover:bg-white hover:border-gray-200'
                                    }
                                `}
                            >
                                <div className={`
                                    w-10 h-10 rounded-xl flex items-center justify-center font-black text-sm transition-all shadow-sm
                                    ${selectedAnswers[currentQuestion.id] === option.id 
                                        ? 'bg-primary-600 text-white' 
                                        : 'bg-white text-gray-400 group-hover:text-primary-600'
                                    }
                                `}>
                                    {String.fromCharCode(65 + idx)}
                                </div>
                                <span className={`text-base font-bold flex-1 ${selectedAnswers[currentQuestion.id] === option.id ? 'text-primary-900' : 'text-gray-600'}`}>
                                    {option.text}
                                </span>
                                {selectedAnswers[currentQuestion.id] === option.id && (
                                    <CheckCircle2 size={24} className="text-primary-600 animate-in zoom-in" />
                                )}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Navigation Controls */}
            <div className="flex items-center justify-between p-4 bg-white rounded-3xl shadow-sm border border-gray-100">
                <button
                    onClick={() => setCurrentQuestionIndex(prev => Math.max(0, prev - 1))}
                    disabled={currentQuestionIndex === 0}
                    className="flex items-center gap-2 px-6 py-3 rounded-2xl font-black text-sm text-gray-400 hover:bg-gray-50 disabled:opacity-0 transition-all"
                >
                    <ChevronRight size={20} /> السابق
                </button>

                {currentQuestionIndex === questions.length - 1 ? (
                    <button
                        onClick={handleSubmit}
                        disabled={submitQuizMutation.isLoading || Object.keys(selectedAnswers).length < questions.length}
                        className="bg-emerald-600 text-white px-10 py-4 rounded-2xl font-black text-sm shadow-xl shadow-emerald-100 hover:bg-emerald-700 transition-all flex items-center gap-2 transform active:scale-95 disabled:opacity-50"
                    >
                        {submitQuizMutation.isLoading ? <LoadingSpinner size="small" /> : <Save size={18} />}
                        إنهاء وتسليم الإجابات
                    </button>
                ) : (
                    <button
                        onClick={() => setCurrentQuestionIndex(prev => Math.min(questions.length - 1, prev + 1))}
                        className="flex items-center gap-2 px-8 py-3 bg-gray-900 text-white rounded-2xl font-black text-sm hover:bg-primary-600 transition-all shadow-xl shadow-gray-200"
                    >
                        السؤال التالي <ChevronLeft size={20} />
                    </button>
                )}
            </div>
        </div>

        {/* Right Column: Status Sidebar (4 Columns) */}
        <div className="lg:col-span-4 space-y-6">
            
            {/* Question Navigator Grid */}
            <div className="bg-white rounded-[2rem] p-8 border border-gray-100 shadow-sm">
                <h3 className="text-lg font-black text-gray-900 mb-6 flex items-center gap-2 tracking-tighter uppercase">
                    <Layers size={18} className="text-primary-600" /> خريطة الأسئلة
                </h3>
                <div className="grid grid-cols-5 gap-3">
                    {questions.map((q, idx) => (
                        <button
                            key={idx}
                            onClick={() => setCurrentQuestionIndex(idx)}
                            className={`
                                w-10 h-10 rounded-xl font-black text-xs transition-all
                                ${currentQuestionIndex === idx ? 'ring-4 ring-primary-50 bg-primary-600 text-white shadow-lg' : 
                                  selectedAnswers[q.id] ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' : 'bg-gray-50 text-gray-400 hover:bg-gray-100'}
                            `}
                        >
                            {idx + 1}
                        </button>
                    ))}
                </div>
            </div>

            {/* Instruction Card */}
            <div className="bg-gray-900 rounded-[2.5rem] p-10 text-white relative overflow-hidden group shadow-2xl">
                <div className="absolute -top-10 -right-10 w-40 h-40 bg-primary-600/20 rounded-full blur-3xl group-hover:scale-150 transition-transform duration-700"></div>
                <div className="relative z-10 space-y-6">
                    <div className="bg-primary-600 w-12 h-12 rounded-2xl flex items-center justify-center shadow-xl shadow-primary-900/50">
                        <ShieldCheck size={24} />
                    </div>
                    <h4 className="text-2xl font-black leading-tight tracking-tight">نظام الحماية <br /> والتركيز</h4>
                    <p className="text-gray-400 text-sm font-medium leading-relaxed">
                        تأكد من اختيار إجابة لكل سؤال. بمجرد ضغط زر التسليم، لن تتمكن من تعديل خياراتك.
                    </p>
                    <div className="pt-4 flex items-center gap-2 text-primary-400 font-black text-[10px] uppercase tracking-widest border-t border-white/5">
                        <Zap size={14} /> Exam Mode Active
                    </div>
                </div>
            </div>

            <div className="bg-amber-50 rounded-[2rem] border border-amber-100 p-6 flex items-start gap-4 shadow-sm animate-in slide-in-from-right duration-1000">
                <div className="p-2 bg-white rounded-xl shadow-sm text-amber-500">
                    <HelpCircle size={20} />
                </div>
                <div className="space-y-1">
                    <h4 className="font-black text-amber-900 text-sm">تحذير!</h4>
                    <p className="text-[10px] font-bold text-amber-700 leading-relaxed uppercase tracking-tighter">الخروج من الصفحة أو تحديثها قد يؤدي إلى فقدان الإجابات غير المرسلة.</p>
                </div>
            </div>
        </div>
      </div>
    </div>
  );
};

export default QuizPage;